import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { RemoveAccessDialog } from "@/components/sharing/remove/RemoveAccessDialog";
import { useRemoveWithUndo } from "@/components/sharing/remove/useRemoveWithUndo";
import { IconUserPlus, IconUsers } from "@tabler/icons-react";
import { Button } from "@/components/lilia-ui/button";
import { Input } from "@/components/lilia-ui/input";
import { Avatar } from "@/components/lilia-ui/avatar";
import { Badge } from "@/components/lilia-ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/lilia-ui/select";
import { useSharingApi } from "@/hooks/useSharingApi";
import { useTeams, useTeamMembers } from "@/hooks/useTeams";
import type { UserCollaborator } from "@/lib/api";
import {
  ASSIGNABLE_ROLES,
  ROLE_LABELS,
  canonicalizeRole,
  roleLabel,
} from "@/lib/sharing/roleLabels";
import {
  buildSharingError,
  classifyShareError,
  type SharingError,
  type SharingErrorAction,
} from "@/lib/sharing/errors";
import { SharingErrorBanner } from "../parts/SharingErrorBanner";
import { PendingInviteRow } from "../pending/PendingInviteRow";
import { useNewArrivals, usePendingInvites } from "../pending/usePendingInvites";
import { peopleSummary } from "@/lib/sharing/pendingInvites";
import { testIds } from "@/testIds";

interface Props {
  documentId: string;
  ownerId?: string;
  collaborators: UserCollaborator[];
  onChange: () => void;
}

// Spec §4 / §5: invite form + people list (owner inside, role
// popover per row). Shared role-assignment list = ASSIGNABLE_ROLES;
// the Remove-from-doc action lives at the bottom of the same menu
// instead of a separate icon, per spec ("hit target competed").
export function InvitePeopleSection({
  documentId,
  ownerId,
  collaborators,
  onChange,
}: Props) {
  const { t } = useTranslation("sharing");
  const sharingApi = useSharingApi();
  const { data: teams } = useTeams();
  // Source the typeahead from the user's first/default team
  // (sibling TeamAccessSection uses the same rule). Multi-team
  // users get a Switch-team affordance in Team access; here we
  // keep the input scope-minimal — typeahead is the user's
  // primary team only, free-form email always works.
  const activeTeam = useMemo(() => {
    if (!teams || teams.length === 0) return null;
    return teams.find((t) => t.isDefault) ?? teams[0];
  }, [teams]);
  const { data: teamMembers } = useTeamMembers(activeTeam?.id ?? "");

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("editor");
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<SharingError | null>(null);
  // Combobox open state — focus opens, blur closes (with a
  // small delay so click-to-pick lands before the close).
  const [comboOpen, setComboOpen] = useState(false);
  // Remove asks first, with Tell them / Let them reply unticked (Olivia, 10 Oct).
  const [removing, setRemoving] = useState<{ userId: string; name: string; role: string } | null>(null);
  const removeWithUndo = useRemoveWithUndo(documentId, onChange);
  // Invitations not yet accepted, after the people with access (Olivia, 10 Oct, §c).
  const [pendingError, setPendingError] = useState<string | null>(null);
  const pending = usePendingInvites(documentId, true, onChange, setPendingError);

  // Owner first, then the people who joined.
  const isOwnerRow = (c: UserCollaborator) => isOwnerOf(c, ownerId);
  const people = useMemo(
    () => [...collaborators].sort((a, b) => Number(isOwnerOf(b, ownerId)) - Number(isOwnerOf(a, ownerId))),
    [collaborators, ownerId],
  );
  const arrived = useNewArrivals(useMemo(() => collaborators.map((c) => c.userId), [collaborators]), true);
  const peopleCount = collaborators.length + (collaborators.some(isOwnerRow) ? 0 : 1);

  const collabIds = useMemo(
    () => new Set(collaborators.map((c) => c.userId)),
    [collaborators],
  );

  // Typeahead matches against the active team's members. Filter:
  // substring on name OR email, case-insensitive. Exclude owner
  // (granting themselves access is meaningless) and people
  // already on the doc (Already-added dedupe handled inline).
  const matches = useMemo(() => {
    const q = email.trim().toLowerCase();
    if (!q) return [];
    const pool = teamMembers ?? [];
    return pool
      .filter((m) => !ownerId || m.userId !== ownerId)
      .filter(
        (m) =>
          (m.name ?? "").toLowerCase().includes(q) ||
          (m.email ?? "").toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [teamMembers, email, ownerId]);

  // Hint surfaces only when there's no match AND the input
  // doesn't yet look like an email. Once it parses as an email,
  // the user is on the free-form path — no hint needed.
  const showNoMatchHint =
    email.trim().length > 0 &&
    matches.length === 0 &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    (teamMembers?.length ?? 0) > 0;

  async function handleInvite() {
    const e = email.trim();
    if (!e) return;
    // Cheap client-side guard so the user gets the §6 invalid-email
    // template instead of waiting on the server round-trip.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      setError(buildSharingError("invalid-email", { email: e }));
      return;
    }
    setInviting(true);
    setError(null);
    try {
      const result = await sharingApi.invite(documentId, { email: e, role });
      if (result.success) {
        setEmail("");
        onChange();
        void pending.refresh();
      } else {
        // Server returned a structured failure. Re-classify so we
        // surface the appropriate template (already-collaborator,
        // quota, etc.) instead of a bare message.
        const kind = classifyShareError(new Error(result.message ?? ""));
        setError(buildSharingError(kind, { email: e, role: roleLabel(role) }));
      }
    } catch (err) {
      setError(buildSharingError(classifyShareError(err), { email: e, role: roleLabel(role) }));
    } finally {
      setInviting(false);
    }
  }

  // Typeahead pick — direct add by userId. Skips the
  // email-lookup roundtrip and avoids "already a collaborator"
  // false alarms (we filter dedupe inline before rendering).
  async function handlePickMember(userId: string) {
    if (collabIds.has(userId)) {
      setEmail("");
      setComboOpen(false);
      return;
    }
    setInviting(true);
    setError(null);
    try {
      await sharingApi.addUserById(documentId, userId, role);
      setEmail("");
      setComboOpen(false);
      onChange();
    } catch (err) {
      setError(buildSharingError(classifyShareError(err), { role: roleLabel(role) }));
    } finally {
      setInviting(false);
    }
  }

  async function handleRoleChange(userId: string, newRole: string) {
    try {
      await sharingApi.updateRole(documentId, userId, newRole);
      onChange();
    } catch (err) {
      setError(buildSharingError(classifyShareError(err)));
    }
  }

  function handleErrorAction(a: SharingErrorAction) {
    switch (a.kind) {
      case "dismiss":
      case "retry":
        setError(null);
        if (a.kind === "retry") void handleInvite();
        return;
      case "copy-link": {
        const url = `${window.location.origin}/d/${documentId}`;
        void navigator.clipboard.writeText(url);
        setError(null);
        return;
      }
      default:
        setError(null);
    }
  }

  return (
    <section data-testid="share-invite-section" className="border-b border-[var(--lilia-border)] pb-5 last:border-b-0">
      <header className="mb-3 flex items-center gap-2">
        <IconUsers size={16} className="text-[var(--lilia-text-secondary)]" />
        <h3 className="text-sm font-semibold text-[var(--lilia-text)]">{t("invite.title")}</h3>
        <span className="ml-auto text-[11px] text-[var(--lilia-text-muted)]">{t("invite.byEmail")}</span>
      </header>

      {error && (
        <div className="mb-3">
          <SharingErrorBanner
            error={error}
            onAction={handleErrorAction}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      <div className="relative">
        <div className="flex items-center gap-2">
          <Input
            data-testid="share-invite-email"
            type="email"
            placeholder={t("invite.placeholder")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setComboOpen(true);
            }}
            onFocus={() => setComboOpen(true)}
            // Slight delay so a click on a typeahead chip lands
            // before the dropdown closes.
            onBlur={() => setTimeout(() => setComboOpen(false), 120)}
            onKeyDown={(e) => e.key === "Enter" && handleInvite()}
            className="flex-1"
          />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="share-invite-role" className="h-9 w-[140px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSIGNABLE_ROLES.map((r) => (
                <SelectItem key={r.value} value={r.value} disabled={r.reserved}>
                  {r.label}
                  {r.reserved && (
                    <span className="ml-2 rounded bg-[var(--lilia-bg)] px-1.5 py-0.5 text-[10px] text-[var(--lilia-text-muted)]">
                      {t("soon")}
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="share-invite-submit"
            variant="primary"
            size="sm"
            onClick={handleInvite}
            disabled={inviting || !email.trim()}
          >
            <IconUserPlus size={14} className="mr-1" />
            {t("invite.invite")}
          </Button>
        </div>

        {/* Typeahead — surfaces team-member matches as the user
            types. Doesn't replace the free-form email path; if
            the input parses as an email and has no match, the
            Invite button just fires the regular invite. */}
        {comboOpen && matches.length > 0 && (
          <ul
            className="absolute left-0 right-[170px] top-full z-10 mt-1 max-h-[220px] overflow-y-auto rounded-md border border-[var(--lilia-border)] bg-[var(--lilia-surface)] py-1 shadow-md"
            role="listbox"
          >
            {matches.map((m) => {
              const display = m.name ?? m.email ?? t("invite.unknown");
              const initials = display.charAt(0).toUpperCase();
              const dedupe = collabIds.has(m.userId);
              return (
                <li key={m.userId}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handlePickMember(m.userId)}
                    disabled={dedupe || inviting}
                    className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm ${
                      dedupe
                        ? "cursor-default text-[var(--lilia-text-muted)]"
                        : "hover:bg-[var(--lilia-bg)]"
                    }`}
                  >
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--lilia-primary,#4f46e5)]/15 text-[10px] font-semibold text-[var(--lilia-primary,#4f46e5)]">
                      {initials}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="text-[var(--lilia-text)]">{display}</span>
                      {m.email && m.name && (
                        <span className="ml-1.5 text-[11px] text-[var(--lilia-text-muted)]">
                          {m.email}
                        </span>
                      )}
                    </span>
                    {dedupe && (
                      <span className="rounded bg-[var(--lilia-bg)] px-1.5 py-0.5 text-[10px] text-[var(--lilia-text-muted)]">
                        {t("invite.alreadyAdded")}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {comboOpen && showNoMatchHint && (
          <p className="mt-1 px-1 text-[11px] text-[var(--lilia-text-muted)]">
            {t("invite.noTeamMatch")}
          </p>
        )}
      </div>

      {/* People list — owner row sits inside (spec §4 point 2) with
          a non-editable Owner badge instead of a separate header. */}
      <p data-testid={testIds.sharing.peopleSummary} className="mt-4 text-xs text-[var(--lilia-text-muted)]">
        {peopleSummary(peopleCount, pending.invites)}
      </p>
      <ul className="mt-1.5 divide-y divide-[var(--lilia-border)] overflow-hidden rounded-lg border border-[var(--lilia-border)]">
        {people.map((c) => {
          const isOwner = isOwnerRow(c);
          const display = c.name ?? c.email ?? t("invite.unknown");
          return (
            <li
              key={c.userId}
              data-testid="share-collaborator"
              data-user-id={c.userId}
              data-role={canonicalizeRole(c.roleName)}
              data-flash={arrived.has(c.userId) ? "true" : undefined}
              className="flex items-center gap-3 px-3 py-2.5 transition-colors duration-700 data-[flash=true]:bg-[var(--lilia-primary-subtle,#e3f0f7)]"
            >
              <Avatar
                src={c.image}
                alt={display}
                fallback={display.charAt(0).toUpperCase()}
                size="sm"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--lilia-text)]">
                  {display}
                </p>
                {c.name && c.email && (
                  <p className="truncate text-xs text-[var(--lilia-text-muted)]">{c.email}</p>
                )}
              </div>
              {isOwner ? (
                <Badge variant="default">{ROLE_LABELS.owner}</Badge>
              ) : (
                <Select
                  value={canonicalizeRole(c.roleName)}
                  onValueChange={(val) =>
                    val === "__remove"
                      ? setRemoving({ userId: c.userId, name: c.name || c.email || t("invite.thisPerson"), role: canonicalizeRole(c.roleName) })
                      : handleRoleChange(c.userId, val)
                  }
                >
                  <SelectTrigger data-testid="share-collaborator-role" data-user-id={c.userId} className="h-8 w-[140px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value} disabled={r.reserved}>
                        {r.label}
                        {r.reserved && (
                          <span className="ml-2 text-[10px] text-[var(--lilia-text-muted)]">
                            {t("soon")}
                          </span>
                        )}
                      </SelectItem>
                    ))}
                    <div className="my-1 h-px bg-[var(--lilia-border)]" />
                    <SelectItem value="__remove">
                      <span className="text-red-600 dark:text-red-400">{t("invite.removeFromDoc")}</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </li>
          );
        })}
        {pending.invites.map((inv) => (
          <PendingInviteRow
            key={inv.id}
            invite={inv}
            note={pending.notes[inv.id]}
            onResend={() => void pending.resend(inv)}
            onCopyLink={() => void pending.copyLink(inv)}
            onCancel={() => void pending.cancel(inv)}
          />
        ))}
      </ul>
      {pendingError && (
        <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
          {pendingError}
        </p>
      )}
      <RemoveAccessDialog
        open={!!removing}
        name={removing?.name ?? ""}
        onCancel={() => setRemoving(null)}
        onConfirm={(choice) => {
          const person = removing;
          setRemoving(null);
          if (person)
            void removeWithUndo(person, choice).catch((err) => setError(buildSharingError(classifyShareError(err))));
        }}
      />
    </section>
  );
}

function isOwnerOf(c: UserCollaborator, ownerId?: string): boolean {
  return c.userId === ownerId || canonicalizeRole(c.roleName) === "owner";
}
